"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const RecruitmentDocumentContext = createContext({
  recruitmentDocuments: [],
});

export const RecruitmentDocumentProvider = ({ children }) => {
  const [recruitmentDocuments, setRecruitmentDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () => localStorage.getItem("token");

  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // ==========================
  // Fetch Recruitment Documents
  // ==========================
  const fetchRecruitmentDocuments = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/recruitment-documents`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch recruitment documents");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setRecruitmentDocuments(data.data || []);
      } else {
        setError(data.message || "Failed to fetch recruitment documents");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to fetch recruitment documents");
    } finally {
      setLoading(false);
    }
  };

  // ==========================
  // Get Single Recruitment Document
  // ==========================
  const getRecruitmentDocument = async (id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/recruitment-documents/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to fetch recruitment document",
        };
      }

      return {
        success: true,
        data: data.data,
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to fetch recruitment document",
      };
    }
  };

  // ==========================
  // Create Recruitment Document
  // payload: { doc_name, related_to, shared_with, shared_date, file, status, notes }
  // ==========================
  const createRecruitmentDocument = async (payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/recruitment-documents`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to create recruitment document",
        };
      }

      if (data.status === 201 || data.status === 200) {
        await fetchRecruitmentDocuments();

        toast.success(data.message || "Recruitment document created successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to create recruitment document",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to create recruitment document",
      };
    }
  };

  // ==========================
  // Update Recruitment Document
  // ==========================
  const updateRecruitmentDocument = async (id, payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/recruitment-documents/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to update recruitment document",
        };
      }

      if (data.status === 200) {
        await fetchRecruitmentDocuments();

        toast.success(data.message || "Recruitment document updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update recruitment document",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to update recruitment document",
      };
    }
  };

  // ==========================
  // Update Recruitment Document Status
  // payload: { status }
  // ==========================
  const updateRecruitmentDocumentStatus = async (id, payload) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/recruitment-documents/${id}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to update recruitment document status",
        };
      }

      if (data.status === 200) {
        await fetchRecruitmentDocuments();

        toast.success(data.message || "Recruitment document status updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update recruitment document status",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to update recruitment document status",
      };
    }
  };

  // ==========================
  // Delete Recruitment Document
  // ==========================
  const deleteRecruitmentDocument = async (id, delete_audit_id) => {
    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/recruitment-documents/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ delete_audit_id }),
      });

      const data = await parseJson(res);

      if (!res.ok || !data) {
        return {
          success: false,
          message: data?.message || "Failed to delete recruitment document",
        };
      }

      if (data.status === 200) {
        await fetchRecruitmentDocuments();

        toast.success(data.message || "Recruitment document deleted successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete recruitment document",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Failed to delete recruitment document",
      };
    }
  };

  useEffect(() => {
    const token = getToken();

    if (token) {
      fetchRecruitmentDocuments();
    }
  }, []);

  return (
    <RecruitmentDocumentContext.Provider
      value={{
        recruitmentDocuments,
        loading,
        error,
        fetchRecruitmentDocuments,
        getRecruitmentDocument,
        createRecruitmentDocument,
        updateRecruitmentDocument,
        updateRecruitmentDocumentStatus,
        deleteRecruitmentDocument,
        setRecruitmentDocuments,
      }}
    >
      {children}
    </RecruitmentDocumentContext.Provider>
  );
};

export const useRecruitmentDocuments = () => useContext(RecruitmentDocumentContext);